import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import CredentialsForm from '../../Component/CredentialsForm';
import PackageDetails from '../../Component/settings/PackageDetails';
import ProfileSettings from '../../Component/settings/ProfileSettings';
import Status from '../../Component/settings/Status';
import Source from '../../Component/settings/Source.js';
import WhatsAppMessages from '../../Component/settings/WhatsAppMessages';
import Email from '../../Component/settings/Email.js';
import AddUserForm from '../../Component/settings/AddUserForm';
import './Settings.css';

const Settings = () => {
  const [userRole, setUserRole] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [apiCredentials, setApiCredentials] = useState({
    username: '',
    password: '',
    userid: '',
    profileid: '',
    apikey: '',
    source: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [packageDetails, setPackageDetails] = useState([]);
  const [statusDetails, setStatusDetails] = useState([]);
  const [sourceDetails, setSourceDetails] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [salesPersonName, setSalesPersonName] = useState('');
  const [salesPersonsLoading, setSalesPersonsLoading] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await apiClient.get('/user-profile');
        setUserRole(response.data.role);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        setUserRole('');
      }
    };
    fetchUserProfile();
  }, []);

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleApiCredentialChange = (e) => {
    const { name, value } = e.target;
    setApiCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleApiCredentialSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      const response = await apiClient.post('/api/save-credentials', apiCredentials);
      setMessage({ 
        text: 'API credentials saved successfully!', 
        type: 'success' 
      });
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                         error.message || 
                         'Failed to save API credentials';
      setMessage({ 
        text: errorMessage, 
        type: 'error' 
      });
      console.error('API Credentials Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSalesPersons = async () => {
    setSalesPersonsLoading(true);
    try {
      const response = await apiClient.get('/api/sales-persons');
      setSalesPersons(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage({
        text: error.response?.data?.error || 'Failed to load sales persons. Apply the sales_persons database migration and restart the backend.',
        type: 'error'
      });
    } finally {
      setSalesPersonsLoading(false);
    }
  };

  const handleSalesPersonSubmit = async (e) => {
    e.preventDefault();
    const name = salesPersonName.trim();
    if (!name) return;
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await apiClient.post('/api/sales-persons', { name });
      setSalesPersons((current) => {
        const updated = [...current.filter((person) => person.id !== response.data.id), response.data];
        return updated.sort((a, b) => a.name.localeCompare(b.name));
      });
      setSalesPersonName('');
      setMessage({ text: response.data.message || 'Sales person added successfully.', type: 'success' });
    } catch (error) {
      setMessage({
        text: error.response?.data?.error || 'Failed to add sales person.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPackageDetails = async () => {
    try {
      const response = await apiClient.get('/api/packages');
      setPackageDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch package details:', error);
      setMessage({
        text: 'Failed to load package details',
        type: 'error'
      });
    }
  };

  const fetchStatusDetails = async () => {
    try {
      const response = await apiClient.get('/api/status');
      setStatusDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch status details:', error);
      setMessage({
        text: 'Failed to load status details',
        type: 'error'
      });
    }
  };

  const fetchSourceDetails = async () => {
    try {
      const response = await apiClient.get('/api/source');
      setSourceDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch stsourceatus details:', error);
      setMessage({
        text: 'Failed to load stsourceatus details',
        type: 'error'
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'salespersons') {
      fetchSalesPersons();
    } else if (activeTab === 'packages') {
      fetchPackageDetails();
    } else if (activeTab === 'status') {
      fetchStatusDetails();
    } else if (activeTab === 'sourcce') {
      fetchSourceDetails();
    }
  }, [activeTab]);

  return (
    <div className="settings-container">
      <h1>Settings</h1>

      <div className="settings-tab-container">
      <div className="settings-tabs">
        <button 
          className={activeTab === 'profile' ? 'active' : ''} 
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <>
          <button
            className={activeTab === 'salespersons' ? 'active' : ''}
            onClick={() => setActiveTab('salespersons')}
          >
            Sales Persons
          </button>
          <button 
            className={activeTab === 'credentials' ? 'active' : ''} 
            onClick={() => setActiveTab('credentials')}
          >
            API Credentials
          </button>
          <button 
            className={activeTab === 'adduser' ? 'active' : ''} 
            onClick={() => setActiveTab('adduser')}
          >
            Add User
          </button>
          <button 
            className={activeTab === 'packages' ? 'active' : ''} 
            onClick={() => setActiveTab('packages')}
          >
            Package Details
          </button>
          <button 
            className={activeTab === 'status' ? 'active' : ''} 
            onClick={() => setActiveTab('status')}
          >
            Status
          </button>
          <button 
            className={activeTab === 'source' ? 'active' : ''} 
            onClick={() => setActiveTab('source')}
          >
            Source
          </button>
          <button 
            className={activeTab === 'whatsapp' ? 'active' : ''} 
            onClick={() => setActiveTab('whatsapp')}
          >
            WhatsApp Messages
          </button>

          <button 
            className={activeTab === 'email' ? 'active' : ''} 
            onClick={() => setActiveTab('email')}
          >
            Email
          </button>
        </>
      </div>
      </div>

      {/* Message display */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="tab-content">
        {activeTab === 'profile' && (
          <ProfileSettings setMessage={setMessage} />
        )}

        {activeTab === 'salespersons' && (
          <section className="settings-section sales-person-settings">
            <h2>Sales Persons</h2>
            <p>Add the team members who handle manufacturer accounts. They will be available in the Sales Person dropdown on the Manufacturers page.</p>
            <form className="sales-person-form" onSubmit={handleSalesPersonSubmit}>
              <label htmlFor="sales-person-name">Sales person name</label>
              <div className="sales-person-form-row">
                <input
                  id="sales-person-name"
                  type="text"
                  value={salesPersonName}
                  onChange={(e) => setSalesPersonName(e.target.value)}
                  maxLength={150}
                  placeholder="Enter full name"
                  required
                />
                <button type="submit" disabled={isLoading || !salesPersonName.trim()}>
                  {isLoading ? 'Saving…' : 'Add Sales Person'}
                </button>
              </div>
            </form>
            <h3>Available sales persons</h3>
            {salesPersonsLoading ? (
              <p>Loading sales persons…</p>
            ) : salesPersons.length ? (
              <ul className="sales-person-list">
                {salesPersons.map((person) => (
                  <li key={person.id}>{person.name}</li>
                ))}
              </ul>
            ) : (
              <p>No sales persons added yet.</p>
            )}
          </section>
        )}

        {activeTab === 'credentials' && (
          <CredentialsForm
            formData={apiCredentials}
            handleInputChange={handleApiCredentialChange}
            saveCredentials={handleApiCredentialSubmit}
            isLoading={isLoading}
            message={message.text}
          />
        )}

        {activeTab === 'adduser' && (
          <AddUserForm setMessage={setMessage} />
        )}

        {activeTab === 'packages' && (
          <PackageDetails packageDetails={packageDetails} />
        )}

        {activeTab === 'status' && (
          <Status statusDetails={statusDetails} setMessage={setMessage} />
        )}

        {activeTab === 'source' && (
          <Source sourceDetails={sourceDetails} setMessage={setMessage} />
        )}

        {activeTab === 'whatsapp' && (
          <WhatsAppMessages />
        )}

        {activeTab === 'email' && (
          <Email />
        )}
      </div>
    </div>
  );
};

export default Settings;
